import { useState, useEffect } from 'react';
import { Plus, X, Radio, HelpCircle, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppContext } from '@/hooks/useAppContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useRelayStatus } from '@/hooks/useRelayStatus';
import { isLocalRelay } from '@/lib/relayProbe';
import { cn } from '@/lib/utils';

interface Relay {
  url: string;
  read: boolean;
  write: boolean;
}

/** Live connection dot — green pulsing when connected, breathing while connecting, grey offline. */
function RelayStatusDot({ url }: { url: string }) {
  const { status, reason } = useRelayStatus(url);

  const label =
    status === 'connected' ? 'Connected' : status === 'connecting' ? 'Connecting…' : 'Offline';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="relative flex-shrink-0 flex items-center justify-center w-4 h-4">
            <span
              aria-hidden="true"
              className={cn(
                'w-2 h-2 rounded-full motion-reduce:animate-none',
                status === 'connected' && 'bg-[#ccff00] animate-status-live',
                status === 'connecting' && 'bg-[#ccff00]/70 animate-status-breathe',
                status === 'offline' && 'bg-white/25',
              )}
            />
            <span className="sr-only">{label}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">
          <p className="text-xs font-medium">{label}</p>
          {reason && status === 'offline' && (
            <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{reason}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function RelayListManager() {
  const { config, updateConfig } = useAppContext();
  const { user } = useCurrentUser();
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();

  const [relays, setRelays] = useState<Relay[]>(config.relayMetadata.relays);
  const [newRelayUrl, setNewRelayUrl] = useState('');

  // Sync local state with config when it changes (e.g., from NostrProvider sync)
  useEffect(() => {
    setRelays(config.relayMetadata.relays);
  }, [config.relayMetadata.relays]);

  const normalizeRelayUrl = (url: string): string => {
    url = url.trim();
    try {
      return new URL(url).toString();
    } catch {
      try {
        return new URL(`wss://${url}`).toString();
      } catch {
        return url;
      }
    }
  };

  const isValidRelayUrl = (url: string): boolean => {
    const trimmed = url.trim();
    if (!trimmed) return false;

    const normalized = normalizeRelayUrl(trimmed);
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  const handleAddRelay = () => {
    if (!isValidRelayUrl(newRelayUrl)) {
      toast({
        title: 'Invalid relay URL',
        description: 'Please enter a valid relay URL (e.g., wss://relay.example.com)',
        variant: 'destructive',
      });
      return;
    }

    const normalized = normalizeRelayUrl(newRelayUrl);

    if (relays.some(r => r.url === normalized)) {
      toast({
        title: 'Relay already exists',
        description: 'This relay is already in your list.',
        variant: 'destructive',
      });
      return;
    }

    const newRelays = [...relays, { url: normalized, read: true, write: true }];
    setRelays(newRelays);
    setNewRelayUrl('');

    saveRelays(newRelays);
  };

  const handleRemoveRelay = (url: string) => {
    const newRelays = relays.filter(r => r.url !== url);
    setRelays(newRelays);
    saveRelays(newRelays);
  };

  const handleToggleRead = (url: string) => {
    const relay = relays.find(r => r.url === url);
    if (!relay) return;

    // Prevent disabling read if write is already disabled
    if (relay.read && !relay.write) {
      toast({
        title: 'Cannot disable read',
        description: 'At least one of read or write must be enabled for each relay.',
        variant: 'destructive',
      });
      return;
    }

    const newRelays = relays.map(r =>
      r.url === url ? { ...r, read: !r.read } : r
    );
    setRelays(newRelays);
    saveRelays(newRelays);
  };

  const handleToggleWrite = (url: string) => {
    const relay = relays.find(r => r.url === url);
    if (!relay) return;

    // Prevent disabling write if read is already disabled
    if (!relay.read && relay.write) {
      toast({
        title: 'Cannot disable write',
        description: 'At least one of read or write must be enabled for each relay.',
        variant: 'destructive',
      });
      return;
    }

    const newRelays = relays.map(r =>
      r.url === url ? { ...r, write: !r.write } : r
    );
    setRelays(newRelays);
    saveRelays(newRelays);
  };

  const saveRelays = (newRelays: Relay[]) => {
    const now = Math.floor(Date.now() / 1000);

    // Update local config
    updateConfig((current) => ({
      ...current,
      relayMetadata: {
        relays: newRelays,
        updatedAt: now,
      },
    }));

    // Publish to Nostr if user is logged in
    if (user) {
      publishNIP65RelayList(newRelays);
    }
  };

  const publishNIP65RelayList = (relayList: Relay[]) => {
    const tags = relayList.map(relay => {
      if (relay.read && relay.write) {
        return ['r', relay.url];
      } else if (relay.read) {
        return ['r', relay.url, 'read'];
      } else if (relay.write) {
        return ['r', relay.url, 'write'];
      }
      // If neither read nor write, don't include (shouldn't happen)
      return null;
    }).filter((tag): tag is string[] => tag !== null);

    publishEvent(
      {
        kind: 10002,
        content: '',
        tags,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Relay list published',
            description: 'Your relay list has been published to Nostr.',
          });
        },
        onError: (error) => {
          console.error('Failed to publish relay list:', error);
          toast({
            title: 'Failed to publish relay list',
            description: 'There was an error publishing your relay list to Nostr.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const renderRelayUrl = (url: string): string => {
    try {
      const parsed = new URL(url);
      if (parsed.protocol === 'wss:') {
        if (parsed.pathname === '/') {
          return parsed.host;
        } else {
          return parsed.host + parsed.pathname;
        }
      } else {
        return parsed.href;
      }
    } catch {
      return url;
    }
  }

  return (
    <div className="space-y-3">
      {/* Relay List */}
      <div className="space-y-2">
        {relays.map((relay) => {
          const local = isLocalRelay(relay.url);
          return (
            <div
              key={relay.url}
              className="flex items-center gap-3 p-3.5 rounded-2xl border border-white/8 bg-white/3 transition-colors hover:border-white/12"
            >
              {/* Live connection dot */}
              <RelayStatusDot url={relay.url} />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="font-mono text-sm text-white truncate"
                    title={relay.url}
                  >
                    {renderRelayUrl(relay.url)}
                  </span>
                  {local && (
                    <span className="flex-shrink-0 text-[9px] font-black uppercase tracking-[0.15em] text-[#ccff00] bg-[#ccff00]/12 border border-[#ccff00]/25 rounded-full px-1.5 py-0.5">
                      Local
                    </span>
                  )}
                </div>

                {/* Read/Write chips — on-brand, subtle */}
                <div className="flex gap-1.5 mt-1.5">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 transition-colors',
                      relay.read
                        ? 'bg-[#ccff00]/12 text-[#ccff00] border border-[#ccff00]/25'
                        : 'bg-white/5 text-white/30 border border-white/8'
                    )}
                  >
                    <ArrowDownToLine className="w-2.5 h-2.5" />
                    Read
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 transition-colors',
                      relay.write
                        ? 'bg-[#ccff00]/12 text-[#ccff00] border border-[#ccff00]/25'
                        : 'bg-white/5 text-white/30 border border-white/8'
                    )}
                  >
                    <ArrowUpFromLine className="w-2.5 h-2.5" />
                    Write
                  </span>
                </div>
              </div>

              {/* Settings popover */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
                    aria-label="Relay read/write settings"
                  >
                    <Radio className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56" align="end">
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor={`read-${relay.url}`} className="text-sm cursor-pointer">
                            Read
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-3 w-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">Fetch content from this relay</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          id={`read-${relay.url}`}
                          checked={relay.read}
                          onCheckedChange={() => handleToggleRead(relay.url)}
                          className="data-[state=checked]:bg-[#ccff00] scale-75"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor={`write-${relay.url}`} className="text-sm cursor-pointer">
                            Write
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-3 w-3 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">Publish your posts to this relay</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Switch
                          id={`write-${relay.url}`}
                          checked={relay.write}
                          onCheckedChange={() => handleToggleWrite(relay.url)}
                          className="data-[state=checked]:bg-[#ccff00] scale-75"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground border-t pt-2">
                      At least one option must be enabled
                    </p>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Remove Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveRelay(relay.url)}
                className="size-6 rounded-full text-muted-foreground hover:text-destructive hover:bg-white/5 flex-shrink-0"
                disabled={relays.length <= 1}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Add Relay Form */}
      <div className="flex gap-2 pt-1">
        <div className="flex-1">
          <Label htmlFor="new-relay-url" className="sr-only">
            Relay URL
          </Label>
          <Input
            id="new-relay-url"
            placeholder="wss://relay.example.com"
            value={newRelayUrl}
            onChange={(e) => setNewRelayUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddRelay();
              }
            }}
            className="rounded-xl bg-white/3 border-white/8 font-mono text-sm h-10 focus-visible:ring-[#ccff00]/40"
          />
        </div>
        <button
          onClick={handleAddRelay}
          disabled={!newRelayUrl.trim()}
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#ccff00] px-4 h-10 text-sm font-black uppercase tracking-wide text-black hover:bg-[#ccff00]/90 active:scale-95 transition-all disabled:opacity-40 disabled:pointer-events-none"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>

      {!user && (
        <p className="text-xs text-muted-foreground pt-1">
          Log in to sync your relay list with Nostr
        </p>
      )}
    </div>
  );
}
